import type {
  CampaignCloseResult,
  CharityOrderSweep,
  CommitmentSweepResult,
  PostpaidSweepResult,
  SeedCampaignPayload,
  TestCampaignDetail,
  TestCampaignSummary,
  TesterAction,
  TesterEventPayload,
  TesterPostpaidStatus,
  TopupStatus,
  TopupSweepResult,
} from "@/interfaces/campaignTester";
import { apiClient } from "@/lib/apiClient";

/**
 * Campaign Tester API. SCAFFOLDING — deleted with the tester.
 *
 * Returns 404 rather than 403 when ENABLE_CAMPAIGN_TESTER is off, so the surface is invisible rather
 * than merely forbidden.
 */

export async function fetchTestCampaigns(): Promise<TestCampaignSummary[]> {
  const { data } = await apiClient.get<{ campaigns: TestCampaignSummary[] }>(
    "/campaign-tester/campaigns",
  );
  return data.campaigns;
}

export async function fetchTestCampaign(campaignId: number): Promise<TestCampaignDetail> {
  const { data } = await apiClient.get<TestCampaignDetail>(
    `/campaign-tester/campaigns/${campaignId}`,
  );
  return data;
}

export async function seedTestCampaign(
  payload: SeedCampaignPayload,
): Promise<{ campaignId: number }> {
  const { data } = await apiClient.post<{ campaign: { campaignId: number } }>(
    "/campaign-tester/campaigns",
    payload,
  );
  return data.campaign;
}

/** Rename a seeded campaign. Title only — nothing else about it is the tester's to edit. */
export async function renameTestCampaign(campaignId: number, title: string): Promise<void> {
  await apiClient.patch(`/campaign-tester/campaigns/${campaignId}`, { title });
}

export async function purgeTestCampaign(campaignId: number): Promise<void> {
  await apiClient.delete(`/campaign-tester/campaigns/${campaignId}`);
}

/**
 * Wind the campaign clock to `asOn` and run the real close.
 *
 * The tester only writes `expire_date`; everything after it is `Campaigns.donateOnExpiry` — the
 * same call a scheduler makes — which is why this can return a "not implemented" error while the
 * donation half is open with the Controller. The campaign is closed regardless.
 */
export async function expireTestCampaign(
  campaignId: number,
  occurredAt?: string,
): Promise<CampaignCloseResult> {
  const { data } = await apiClient.post<{ result: CampaignCloseResult }>(
    `/campaign-tester/campaigns/${campaignId}/expire`,
    { occurredAt },
  );
  return data.result;
}

/**
 * Force every live commitment's window shut, then run the real sweep.
 *
 * A recipient window is 30–90 days; this closes them so the end of one can be seen now. What
 * happens next is the campaign's own rule — donate to its causes, or roll the credits back.
 */
export async function expireTestCommitments(
  campaignId: number,
  occurredAt?: string,
): Promise<CommitmentSweepResult[]> {
  const { data } = await apiClient.post<{ results: CommitmentSweepResult[] }>(
    `/campaign-tester/campaigns/${campaignId}/commitments/expire`,
    { occurredAt },
  );
  return data.results;
}

/**
 * Buy the charity products for this campaign's donations.
 *
 * The last step of the donation path, and the one a seeded campaign cannot reach on its own: a test
 * campaign carries `no_shopify_orders`, so the hourly sweep skips it and a direct call refuses it.
 * Pressing this opts THIS campaign in — a person choosing to buy, which is the distinction that
 * guard exists to protect.
 *
 * The orders are real. Shopify has no test mode here.
 */
export async function placeTestCharityOrders(campaignId: number): Promise<CharityOrderSweep> {
  const { data } = await apiClient.post<{ result: CharityOrderSweep }>(
    `/campaign-tester/campaigns/${campaignId}/charity-orders`,
    {},
  );
  return data.result;
}

/** Drive one lifecycle event for one contact. `occurredAt` is the simulated clock. */
export async function runTesterAction(
  action: TesterAction,
  { campaignId, contactId, occurredAt, externalRef, credits }: TesterEventPayload,
): Promise<void> {
  await apiClient.post(`/campaign-tester/campaigns/${campaignId}/${action}/${contactId}`, {
    occurredAt,
    externalRef,
    credits,
  });
}

/**
 * Where an automated campaign stands against its margin. 200 with `configured: false` for a
 * prepaid campaign — not an error, just nothing to report.
 */
export async function fetchTestTopupStatus(campaignId: number): Promise<TopupStatus> {
  const { data } = await apiClient.get<{ status: TopupStatus }>(
    `/campaign-tester/campaigns/${campaignId}/topup-status`,
  );
  return data.status;
}

/**
 * Run the hourly top-up sweep against this campaign alone — the cron, on demand.
 *
 * `dryRun` prices and records the decision without charging, which is the safe way to see whether
 * a call is due. `decline` makes the simulated card fail with that code instead of settling, so the
 * failure and recovery path can be walked rather than only read about.
 *
 * Both travel as QUERY PARAMS because that is what the route reads. They were sent as a body
 * before, which the route never looked at — so "dry run" charged.
 */
export async function runTestTopup(
  campaignId: number,
  opts: { dryRun?: boolean; decline?: string } = {},
): Promise<TopupSweepResult> {
  const params = new URLSearchParams();
  if (opts.dryRun) params.set("dryRun", "1");
  if (opts.decline) params.set("decline", opts.decline);
  const qs = params.toString();
  const { data } = await apiClient.post<{ result: TopupSweepResult }>(
    `/campaign-tester/campaigns/${campaignId}/topups${qs ? `?${qs}` : ""}`,
  );
  return data.result;
}

/** Where a postpaid campaign stands. Null for any other funding model. */
export async function fetchTestPostpaidStatus(
  campaignId: number,
): Promise<TesterPostpaidStatus | null> {
  const { data } = await apiClient.get<{ status: TesterPostpaidStatus | null }>(
    `/campaign-tester/campaigns/${campaignId}/postpaid-status`,
  );
  return data.status;
}

/**
 * Run the daily postpaid invoice sweep against this campaign alone. `occurredAt` is the simulated
 * clock — the sweep raises what is due on that date. The final invoice is real (Stripe test mode).
 */
export async function runTestPostpaidSweep(
  campaignId: number,
  occurredAt?: string,
): Promise<PostpaidSweepResult> {
  const { data } = await apiClient.post<{ result: PostpaidSweepResult }>(
    `/campaign-tester/campaigns/${campaignId}/postpaid/invoice-sweep`,
    { occurredAt },
  );
  return data.result;
}

/** Record one of this campaign's invoices paid in full — Finance's "payment received". */
export async function recordTestInvoicePayment(
  campaignId: number,
  invoiceId: number,
): Promise<void> {
  await apiClient.post(
    `/campaign-tester/campaigns/${campaignId}/invoices/${invoiceId}/payments`,
    {},
  );
}
